import { useState } from 'react';
import Button from '@/components/Button';
import CurrencySelectBox from '@/components/CurrencySelectBox';
import LanguageSelectBox from '@/components/LanguageSelectBox';
import PhoneNumberInput from '@/components/PhoneNumberInput';
import RetailerSelectBox from '@/components/RetailerSelectBox';
import StoreSelectBox from '@/components/StoreSelectBox';
import SupplierSelectBox from '@/components/SupplierSelectBox';
import TimezoneSelectBox from '@/components/TimezoneSelectBox';
import ToggleSwitch from '@/components/ToggleSwitch';
import UserRoleSelectBox from '@/components/UserRoleSelectBox';
import { formatUserRoleForAdmin, User, UserRole } from '@/models/User';
import OrganizationSelectBox from '@/modules/admin/components/OrganizationSelectBox';
import { CheckIcon } from '@heroicons/react/24/solid';
import { Controller, useForm } from 'react-hook-form';

interface UserFormProps {
  user?: User;
  onSubmit: (user: User) => Promise<void>;
}

const UserForm = ({ user, onSubmit }: UserFormProps) => {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<User>({
    defaultValues: user
      ? { ...user }
      : {
          id: '',
          username: '',
          password: '',
          email: '',
          firstName: '',
          lastName: '',
          fullName: '',
          currencyCode: 'SGD',
          languageCode: 'en',
          mobileCountryCode: '+65',
          mobileNumber: '',
          timezone: 'Asia/Singapore',
          role: UserRole.StoreManager,
          status: 'ACTIVE',
          createdAt: new Date(),
          organizationId: undefined,
          supplierId: undefined,
          retailerId: undefined,
          storeId: undefined,
        },
  });

  const onSubmitForm = async (data: User) => {
    setLoading(true);
    try {
      await onSubmit(data);
    } finally {
      setLoading(false);
    }
  };

  const role = watch('role');
  const retailerId = watch('retailerId');

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
      {/* Account Info */}
      <div className="p-4 border rounded-md shadow-sm bg-white">
        <h3 className="text-lg font-semibold mb-4">Account Info</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label htmlFor="username" className="block font-medium text-gray-700">
              Username:
            </label>
            <input
              type="text"
              id="username"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              {...register('username')}
              className="mt-1 p-2 border border-gray-300 rounded-md w-full"
            />
          </div>
          <div className="col-span-2">
            <label htmlFor="password" className="block font-medium text-gray-700">
              {user?.id ? 'New Password:' : 'Password:'}
            </label>
            <input
              type="password"
              id="password"
              autoComplete="new-password"
              {...register('password')}
              className="mt-1 p-2 border border-gray-300 rounded-md w-full"
            />
          </div>
          <div className="col-span-2">
            <label htmlFor="email" className="block font-medium text-gray-700">
              Email:
            </label>
            <input
              type="email"
              id="email"
              {...register('email')}
              className="mt-1 p-2 border border-gray-300 rounded-md w-full"
            />
          </div>
        </div>
      </div>

      {/* Personal Info */}
      <div className="p-4 border rounded-md shadow-sm bg-white">
        <h3 className="text-lg font-semibold mb-4">Personal Info</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block font-medium text-gray-700">
              First Name:
            </label>
            <input
              type="text"
              id="firstName"
              {...register('firstName')}
              className="mt-1 p-2 border border-gray-300 rounded-md w-full"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block font-medium text-gray-700">
              Last Name:
            </label>
            <input
              type="text"
              id="lastName"
              {...register('lastName')}
              className="mt-1 p-2 border border-gray-300 rounded-md w-full"
            />
          </div>
          <div>
            <label htmlFor="fullName" className="block font-medium text-gray-700">
              Full Name:
            </label>
            <input
              type="text"
              id="fullName"
              {...register('fullName')}
              className="mt-1 p-2 border border-gray-300 rounded-md w-full"
            />
          </div>
          <div>
            <label htmlFor="mobileNumber" className="block font-medium text-gray-700">
              Mobile Number:
            </label>
            <PhoneNumberInput
              countryCode={user?.mobileCountryCode}
              onChange={(countryCode) => setValue('mobileCountryCode', countryCode)}
              id="mobileNumber"
              inputProps={register('mobileNumber', { required: true })}
            />
            {errors.mobileNumber && <p className="text-red-700 text-sm">Invalid mobile number.</p>}
          </div>
        </div>
      </div>

      {/* Role & Status */}
      <div className="p-4 border rounded-md shadow-sm bg-white">
        <h3 className="text-lg font-semibold mb-4">Role & Status</h3>
        <div className="grid grid-cols-2 gap-4 items-center">
          <Controller
            name="role"
            control={control}
            render={({ field: { value, onChange } }) => (
              <UserRoleSelectBox
                value={value}
                onSelect={(selected) => {
                  onChange(selected?.id);
                  if (selected?.id === UserRole.SupplierAdmin) {
                    setValue('retailerId', undefined);
                    setValue('storeId', undefined);
                  } else if (selected?.id === UserRole.StoreManager) {
                    setValue('supplierId', undefined);
                  }
                }}
                formatUserRole={formatUserRoleForAdmin}
                requireSelection
              />
            )}
          />
          <Controller
            name="status"
            control={control}
            render={({ field: { value, onChange } }) => (
              <ToggleSwitch
                checked={value === 'ACTIVE'}
                onChange={(checked) => onChange(checked ? 'ACTIVE' : 'INACTIVE')}
                labelText="Status:"
              />
            )}
          />
        </div>
      </div>

      {/* Preferences */}
      <div className="p-4 border rounded-md shadow-sm bg-white">
        <h3 className="text-lg font-semibold mb-4">Preferences</h3>
        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="languageCode"
            control={control}
            render={({ field: { value, onChange } }) => (
              <LanguageSelectBox
                value={value || null}
                onSelect={(selected) => onChange(selected?.id)}
              />
            )}
          />
          <Controller
            name="timezone"
            control={control}
            render={({ field: { value, onChange } }) => (
              <TimezoneSelectBox
                value={value}
                onSelect={(selected) => onChange(selected?.id)}
                requireSelection
              />
            )}
          />
          <Controller
            name="currencyCode"
            control={control}
            render={({ field: { value, onChange } }) => (
              <CurrencySelectBox
                value={value}
                onSelect={(selected) => onChange(selected?.id)}
                requireSelection
              />
            )}
          />
        </div>
      </div>

      {/* Associations */}
      <div className="p-4 border rounded-md shadow-sm bg-white">
        <h3 className="text-lg font-semibold mb-4">Organization</h3>
        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="organizationId"
            control={control}
            render={({ field: { value, onChange } }) => (
              <OrganizationSelectBox
                value={value || null}
                onSelect={(selected) => onChange(selected?.id)}
              />
            )}
          />

          {/** Show Supplier only if role is Supplier */}
          {role === UserRole.SupplierAdmin && (
            <Controller
              name="supplierId"
              control={control}
              render={({ field: { value, onChange } }) => (
                <SupplierSelectBox
                  value={value || null}
                  onSelect={(selected) => onChange(selected?.id)}
                />
              )}
            />
          )}

          {/** Show Retailer + Store only if role is Retailer */}
          {(role === UserRole.RetailerAdmin || role === UserRole.StoreManager) && (
            <>
              <Controller
                name="retailerId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <RetailerSelectBox
                    value={value || null}
                    onSelect={(selected) => onChange(selected?.id)}
                  />
                )}
              />
              {role === UserRole.StoreManager && (
                <Controller
                  name="storeId"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <StoreSelectBox
                      value={value || null}
                      onSelect={(selected) => onChange(selected?.id)}
                      retailerId={retailerId}
                    />
                  )}
                />
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="submit"
          icon={<CheckIcon className="h-6 w-6" aria-hidden="true" />}
          loading={loading}
          loadingIconClassName="h-5 w-5 mr-1"
          variant="primary"
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default UserForm;
